import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { Commission } from '@/services/commissions/commissions.types';
import * as React from 'react';
import { useCommissionStatusToggle } from '../hooks/useCommissionStatusToggle';
import { ManageCommissionCategoryForm } from '../ManageCommissionCategoryForm';
import { getColumns } from './Column';
import { useCan } from '@/components/can/Can';
import { PERMS } from '@/features/auth/permissions/permissionKeys';

export function CommissionsTable({
  data,
  pagination,
  isLoading = false,
}: {
  data: Commission[];
  pagination: Pagination;
  isLoading: boolean;
}) {
  const { openConfirmation } = useConfirmationStore();
  const { onOpen } = useDialogFormStore();
  const canAddNew = useCan({ allOf: [PERMS.commission.category.manage] });

  const { t } = useDynamicLocale();
  const { mutateAsync } = useCommissionStatusToggle();

  const columns = React.useMemo(
    () => getColumns(onOpen, mutateAsync, t, openConfirmation),
    [onOpen, mutateAsync, t, openConfirmation],
  );
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const onAddNew = () => {
    onOpen({
      title: 'Manage Commission',
      okText: <CrumbTitle id="common.page.button.submit" />,
      size: 'lg',
      modalTitle: 'configurationManagement.commissions.manageCommissionModal.title',
      component: <ManageCommissionCategoryForm />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'categoryId', desc: true }],
    },
    meta: {
      title: <CrumbTitle id="configurationManagement.page.table.commission" />,
      action: canAddNew ? onAddNew : null,
      actionKey: canAddNew ? 'onAddNew' : '',
      totalPage: pagination.totalItems,
      loading: isLoading,
    },

    getRowId: (originalRow) => String(originalRow.categoryId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="font-medium">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
